import { parseISO } from 'date-fns/parseISO'
import React from 'react'
import { LuCalendarPlus2, LuTriangleAlert } from 'react-icons/lu'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatDate } from '@/lib/helpers/dateHelpers'
import {
  BulkUpdatePayload,
  ReferralsImportItem,
  ReferralsImportItemPartial,
} from '@/redux/slices/referralsImportBase'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
  bulkUpdateItems: (payload: BulkUpdatePayload) => void
  bulkUpdateSelectedOnly: boolean
}

const ReferralDateCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  bulkUpdateItems,
  bulkUpdateSelectedOnly,
}) => {
  const [isEdit, setEdit] = React.useState(false)
  const isSet = !!importItem.referralDate
  const hasSourcedNotUsed = !isSet && !!importItem.sourceDateReferred
  const handleChange = (newDate: Date | undefined) => {
    updateImportItem({
      refUUID: importItem.refUUID,
      referralDate: newDate ? newDate.toISOString() : null,
    })
    setEdit(false)
  }
  const handleBulkApply = () => {
    bulkUpdateItems({
      filter: {
        selected: bulkUpdateSelectedOnly,
      },
      data: {
        referralDate: importItem.referralDate,
      },
    })
  }
  const bulkUpdateScope = !bulkUpdateSelectedOnly
    ? 'all'
    : importItem.selected
      ? 'selected'
      : 'none'
  const maxDate = new Date()
  maxDate.setHours(0)
  maxDate.setMinutes(0)
  maxDate.setSeconds(0)
  maxDate.setMilliseconds(0)

  const date = importItem.referralDate
    ? parseISO(importItem.referralDate)
    : undefined

  return (
    <>
      <Popover open={isEdit} onOpenChange={setEdit}>
        <PopoverTrigger asChild>
          {isSet ? (
            <span>
              {importItem.referralDate
                ? formatDate(importItem.referralDate)
                : ''}
            </span>
          ) : (
            <Button
              variant="outline"
              size="xs"
              id="date"
              className="justify-start font-normal"
            >
              <LuCalendarPlus2 />
              Select date
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="z-1050 w-auto overflow-hidden p-0"
          align="start"
        >
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            captionLayout="dropdown"
            onSelect={(date) => {
              handleChange(date)
              setEdit(false)
            }}
          />
        </PopoverContent>
      </Popover>

      {isSet && (
        <>
          <span className="show-on-row-hover text-xs">
            {', '}
            <Button variant="plain" onClick={() => setEdit(true)}>
              change
            </Button>

            {bulkUpdateScope == 'all' && (
              <>
                {' or '}
                <Button variant="plain" onClick={handleBulkApply}>
                  apply&nbsp;to&nbsp;all
                </Button>
              </>
            )}

            {bulkUpdateScope == 'selected' && (
              <>
                {' or '}
                <Button variant="plain" onClick={handleBulkApply}>
                  apply&nbsp;to&nbsp;selected
                </Button>
              </>
            )}
          </span>
        </>
      )}

      {hasSourcedNotUsed && (
        <span className="text-warning mt-1 block text-xs">
          <LuTriangleAlert className="inline-block -translate-y-px" /> problem
          with using source date, select date manually
        </span>
      )}
    </>
  )
}

export default ReferralDateCell
